import {useEffect} from 'react';
import {AutoFieldHelpers, useAutoField} from '../../../components/AutoForm/useAutoForm';
import {BuilderPot} from '../../../schema-types/BuilderPot';
import {OvbuilderDetails_ovrecommendDetails} from '../../../schema-types/OvbuilderDetails';
import {
  AnnuityIncomeFrequencyEnum,
  AnnuityPaymentTypeEnum,
  AnnuityRateTypeEnum,
  AnnuityTypeEnum,
} from '../../../schema-types/globalTypes';
import {setUpAnnuityFields} from '../BuilderSetUpNew/builderAnnuityFields';
import {
  findParentOfChild,
  optionalRound,
} from '../CreateBuilder/BuilderSourceDestination/builderUtils';
import {getFeeFormikFromServer} from '../CreateBuilder/BuilderDAInitialFees';
import {UPDATES} from '../../../shared/UPDATES';

const defaultAnnuityValues: setUpAnnuityFields = {
  annuityType: AnnuityTypeEnum.PERSONAL_PENSION_ANNUITY,
  incomeFrequency: AnnuityIncomeFrequencyEnum.MONTHLY,
  rateType: AnnuityRateTypeEnum.ORDINARY_RATES,
  paymentType: AnnuityPaymentTypeEnum.IN_ARREARS,
};

export const getAnnuityInitialValues = (
  builderDetails: OvbuilderDetails_ovrecommendDetails | null,
  isSPPDA: boolean
): setUpAnnuityFields => {
  if (!builderDetails?.annuity) {
    return defaultAnnuityValues;
  }
  const saved = builderDetails.annuity;
  const sourcePot = saved.pot ?? undefined;
  const sourceId = sourcePot?.id;

  const annuity: setUpAnnuityFields = {
    notes: builderDetails.note ?? undefined,
    sourceId,
    parentPotId: sourcePot?.parent?.id ?? undefined,
    annuityType: saved.annuityType ?? undefined,
    incomeFrequency: saved.incomeFrequency ?? undefined,
    guaranteePeriod: saved.guaranteedPeriod ?? undefined,
    spouseAnnuityPercent: saved.spouseAnnuityPercent ?? undefined,
    rateType: saved.rateType ?? undefined,
    indexLinked: saved.isIndexLinked ?? undefined,
    longTermCareCapitalProtected: saved.isLtcCapitalProtected ?? undefined,
    ltcProtectedPercent: saved.protectedPercentage ?? undefined,
    paymentType: saved.paymentType ?? undefined,
    initialFee: saved.initialFee?.id ?? undefined,
  };

  if (!sourceId || !sourcePot) {
    return annuity;
  }

  return {
    ...annuity,
    singleClientAmount: optionalRound(saved.singleClientAmount),
    full: saved.isFull ?? false,
    legacyInitialFee: getFeeFormikFromServer(isSPPDA, saved.sppInitialFee ?? null),
  };
};

export const changeAnnuitySourceFactory = ({
  sourcePots,
  annuityHelpers,
  annuityValue,
}: {
  sourcePots: BuilderPot[];
  annuityHelpers: AutoFieldHelpers<setUpAnnuityFields | undefined>;
  annuityValue: setUpAnnuityFields | undefined;
}) => {
  return (id: string) => {
    const parent = findParentOfChild(sourcePots, id);
    annuityHelpers.setValue(UPDATES._702, {
      ...annuityValue,
      sourceId: id,
      parentPotId: parent?.id,
      singleClientAmount: '',
      full: false,
      legacyInitialFee: null,
    });
  };
};

export const useSetAnnuityFullAmount = ({maxAmount}: {maxAmount?: number}): void => {
  const [fullField] = useAutoField<setUpAnnuityFields['full']>('annuity.full');
  const [_, __, amountHelpers] = useAutoField<setUpAnnuityFields['singleClientAmount']>(
    'annuity.singleClientAmount'
  );
  useEffect(() => {
    if (fullField.value && maxAmount != null) {
      amountHelpers.setValue(UPDATES._705, maxAmount);
    }
  }, [fullField.value, maxAmount, amountHelpers]);
};
